import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import api from '../lib/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart,
} from 'recharts';

const fmtDate = (d) => {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return d;
};

const COLORS = ['#059669', '#2563eb', '#7c3aed', '#dc2626', '#d97706', '#0891b2', '#be185d', '#4f46e5', '#65a30d', '#ea580c'];

const STATUS_COLORS = {
  draft: '#9ca3af', confirmed: '#3b82f6', partial_received: '#f59e0b',
  completed: '#10b981', cancelled: '#ef4444',
};

const PODashboard = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      const res = await api.get('/po-dashboard');
      setData(res.data);
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
    </div>
  );

  if (!data) return <div className="p-6 text-center text-gray-500">No data available</div>;

  const { kpis, monthly_trends, vendor_breakdown, brand_breakdown, delivery_timeline, recent_pos } = data;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white p-2.5 rounded-lg shadow-lg border text-xs">
        <p className="font-semibold text-gray-900 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>
            {p.name}: {typeof p.value === 'number' && p.name.toLowerCase().includes('amount')
              ? `₹${p.value.toLocaleString('en-IN')}`
              : p.value.toLocaleString('en-IN')}
          </p>
        ))}
      </div>
    );
  };

  const statusData = Object.entries(kpis.status_counts || {}).map(([name, value]) => ({
    name: name.replace('_', ' '), value, fill: STATUS_COLORS[name] || '#9ca3af',
  }));

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="po-dashboard-container">
      <div>
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>PO Dashboard</h1>
        <p className="text-gray-600 mt-1">Purchase order insights and analytics</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-cards">
        <Card className="border-l-4 border-l-emerald-500 hover:shadow-md transition-shadow">
          <CardContent className="p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Total POs</p>
            <p className="text-xl font-bold text-gray-900 mt-1" data-testid="kpi-total-pos">{kpis.total_pos}</p>
            <div className="flex gap-2 text-[10px] mt-1">
              <span className="text-green-600">{kpis.po_completed || 0} done</span>
              <span className="text-amber-600">{kpis.po_pending || 0} pending</span>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow">
          <CardContent className="p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Value Ordered</p>
            <p className="text-xl font-bold text-gray-900 mt-1" data-testid="kpi-value-ordered">₹{(kpis.value_ordered || 0).toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500 hover:shadow-md transition-shadow">
          <CardContent className="p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Value Received</p>
            <p className="text-xl font-bold text-green-700 mt-1" data-testid="kpi-value-received">₹{(kpis.value_received || 0).toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500 hover:shadow-md transition-shadow">
          <CardContent className="p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Value Pending</p>
            <p className="text-xl font-bold text-amber-700 mt-1" data-testid="kpi-value-pending">₹{(kpis.value_pending || 0).toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500 hover:shadow-md transition-shadow">
          <CardContent className="p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Value Cancelled</p>
            <p className="text-xl font-bold text-red-700 mt-1" data-testid="kpi-value-cancelled">₹{(kpis.value_cancelled || 0).toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-violet-500 hover:shadow-md transition-shadow">
          <CardContent className="p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Total Pieces</p>
            <p className="text-xl font-bold text-gray-900 mt-1" data-testid="kpi-total-pieces">{(kpis.total_pieces || 0).toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Monthly Order Trends */}
        <Card className="lg:col-span-2" data-testid="monthly-trends-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Monthly Order Trends</CardTitle>
          </CardHeader>
          <CardContent>
            {monthly_trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={monthly_trends} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="count" fill="#059669" name="PO Count" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="amount" fill="#3b82f6" name="Amount (₹)" radius={[4, 4, 0, 0]} opacity={0.7} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-sm text-gray-400 py-12">No monthly data yet</p>
            )}
          </CardContent>
        </Card>

        {/* PO Status Distribution */}
        <Card data-testid="status-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">PO Status</CardTitle>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={55} outerRadius={90}
                    paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`}
                    labelLine={{ stroke: '#999', strokeWidth: 0.5 }} >
                    {statusData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-sm text-gray-400 py-12">No status data</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Vendor Spend Breakdown */}
        <Card data-testid="vendor-spend-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Vendor-wise Spend</CardTitle>
          </CardHeader>
          <CardContent>
            {vendor_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={vendor_breakdown.slice(0, 8)} cx="50%" cy="50%" outerRadius={95}
                    dataKey="amount" nameKey="name"
                    label={({ name, percent }) => `${name.length > 12 ? name.slice(0, 12) + '…' : name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: '#999', strokeWidth: 0.5 }}>
                    {vendor_breakdown.slice(0, 8).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(val) => `₹${val.toLocaleString('en-IN')}`} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-sm text-gray-400 py-12">No vendor data</p>
            )}
          </CardContent>
        </Card>

        {/* Brand Spend Breakdown */}
        <Card data-testid="brand-spend-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Brand-wise Spend</CardTitle>
          </CardHeader>
          <CardContent>
            {brand_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={brand_breakdown.slice(0, 8)} layout="vertical" margin={{ top: 5, right: 20, left: 60, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={55} />
                  <Tooltip formatter={(val) => `₹${val.toLocaleString('en-IN')}`} />
                  <Bar dataKey="amount" name="Spend (₹)" radius={[0, 4, 4, 0]}>
                    {brand_breakdown.slice(0, 8).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-sm text-gray-400 py-12">No brand data</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pieces Trend Area Chart */}
      {monthly_trends.length > 0 && (
        <Card data-testid="pieces-trend-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Monthly Pieces Ordered</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={monthly_trends} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="piecesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="pieces" stroke="#7c3aed" fill="url(#piecesGrad)" name="Pieces" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Delivery Timeline */}
      {delivery_timeline.length > 0 && (
        <Card data-testid="delivery-timeline">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Delivery Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 text-left">PO</th>
                    <th className="p-2 text-left">Design</th>
                    <th className="p-2 text-left">Brand</th>
                    <th className="p-2 text-left">Vendor</th>
                    <th className="p-2 text-left">Delivery Date</th>
                    <th className="p-2 text-right">Ordered</th>
                    <th className="p-2 text-right">Received</th>
                    <th className="p-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {delivery_timeline.map((d, i) => {
                    const pct = d.qty_ordered > 0 ? Math.round((d.qty_received / d.qty_ordered) * 100) : 0;
                    const isOverdue = new Date(d.delivery_date) < new Date() && d.status !== 'received' && d.status !== 'closed';
                    return (
                      <tr key={i} className={`border-t ${isOverdue ? 'bg-red-50' : ''}`}>
                        <td className="p-2 font-mono font-medium text-emerald-700">{d.po_number}</td>
                        <td className="p-2 font-medium">{d.design_code}</td>
                        <td className="p-2">{d.brand}</td>
                        <td className="p-2">{d.vendor}</td>
                        <td className="p-2">
                          <span className={isOverdue ? 'text-red-600 font-semibold' : ''}>{fmtDate(d.delivery_date)}</span>
                          {isOverdue && <span className="ml-1 text-red-500 text-[10px]">OVERDUE</span>}
                        </td>
                        <td className="p-2 text-right">{d.qty_ordered}</td>
                        <td className="p-2 text-right">
                          <span className={pct >= 100 ? 'text-green-600 font-semibold' : ''}>{d.qty_received}</span>
                          <span className="text-gray-400 ml-1">({pct}%)</span>
                        </td>
                        <td className="p-2">
                          <Badge className={`text-[10px] ${
                            d.status === 'received' ? 'bg-green-100 text-green-700' :
                            d.status === 'dispatched' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{d.status}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent POs */}
      <Card data-testid="recent-pos-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Recent Purchase Orders</CardTitle>
        </CardHeader>
        <CardContent>
          {recent_pos.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 text-left">PO Number</th>
                    <th className="p-2 text-left">Vendor</th>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-center">Items</th>
                    <th className="p-2 text-right">Amount</th>
                    <th className="p-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_pos.map((po, i) => (
                    <tr key={i} className="border-t hover:bg-gray-50">
                      <td className="p-2 font-mono font-bold text-emerald-700">{po.po_number}</td>
                      <td className="p-2">{po.vendor}</td>
                      <td className="p-2 text-gray-500">{fmtDate(po.date)}</td>
                      <td className="p-2 text-center">{po.items}</td>
                      <td className="p-2 text-right font-semibold">₹{(po.amount || 0).toLocaleString('en-IN')}</td>
                      <td className="p-2 text-center">
                        <Badge className={`text-xs ${
                          po.status === 'completed' ? 'bg-green-100 text-green-700' :
                          po.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                          po.status === 'partial_received' ? 'bg-amber-100 text-amber-700' :
                          po.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{po.status?.replace('_', ' ')}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-sm text-gray-400 py-8">No purchase orders yet</p>
          )}
        </CardContent>
      </Card>

      {/* Vendor Summary Table */}
      {vendor_breakdown.length > 0 && (
        <Card data-testid="vendor-summary-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Vendor Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 text-left">Vendor</th>
                    <th className="p-2 text-right">Orders</th>
                    <th className="p-2 text-right">Pieces</th>
                    <th className="p-2 text-right">Total Spend</th>
                    <th className="p-2 text-right">Avg/Order</th>
                  </tr>
                </thead>
                <tbody>
                  {vendor_breakdown.map((v, i) => (
                    <tr key={i} className="border-t hover:bg-gray-50">
                      <td className="p-2 font-medium flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }}></div>
                        {v.name}
                      </td>
                      <td className="p-2 text-right">{v.orders}</td>
                      <td className="p-2 text-right">{v.pieces.toLocaleString('en-IN')}</td>
                      <td className="p-2 text-right font-semibold">₹{v.amount.toLocaleString('en-IN')}</td>
                      <td className="p-2 text-right text-gray-500">₹{v.orders > 0 ? Math.round(v.amount / v.orders).toLocaleString('en-IN') : 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PODashboard;
